<template>
  <PrimeDialog
    v-model:visible="visible"
    modal
    :dismissable-mask="false"
    :close-on-escape="true"
    :closable="false"
    :unstyled="false"
    :pt="passThrough"
    :appendTo="props.appendTo"
  >
    <!-- Header -->
    <template #header>
      <div
        :class="[...headerClasses]"
        v-bind="getPt('headerWrapper', passThrough)"
      >
        <div class="flex items-center gap-1 w-full">
          <MeridiaIcon
            v-if="iconName"
            :name="iconName"
            type="material-symbols-outlined"
            class="icon-m flex-shrink-0"
            :class="[...titleClasses]"
            v-bind="getPt('icon', passThrough)"
          />
          <div class="flex-1">
            <div>
              <slot name="title">
                <div
                  :class="[...titleClasses]"
                  v-bind="getPt('title', passThrough)"
                >
                  {{ props.title }}
                </div>
              </slot>
            </div>
          </div>
        </div>
        <slot name="description">
          <div
            v-if="props.subtitle"
            class="text-neutral-text-body text-body-base pt-m w-full"
            v-bind="getPt('subtitle', passThrough)"
          >
            {{ props.subtitle }}
          </div>
        </slot>
      </div>
    </template>

    <!-- Content -->
    <div
      v-if="props.type !== DIALOG_TYPES.BASE || $slots.body"
      class="px-xl pt-m pb-xs"
      v-bind="getPt('contentWrapper', passThrough)"
    >
      <slot name="body">
        <template v-if="props.type === DIALOG_TYPES.RADIO">
          <div class="flex flex-col gap-xs">
            <MeridiaRadioButton
              v-for="opt in props.options || []"
              :key="opt.value"
              v-model="selected"
              :name="dialogId"
              :value="opt.value"
              :label="opt.label"
              size="small"
              v-bind="getPt('radio', passThrough)"
            />
          </div>
        </template>
        <template v-else-if="props.type === DIALOG_TYPES.MULTIPLE_SELECT">
          <div class="flex flex-col gap-xs">
            <MeridiaCheckbox
              v-for="opt in props.options || []"
              :key="opt.value"
              :label="opt.label"
              size="small"
              v-model="selectedListMap[opt.value]"
              v-bind="getPt('checkbox', passThrough)"
            />
          </div>
        </template>
        <template v-else>
          <!-- base: empty by default unless slot provided -->
        </template>
      </slot>
    </div>

    <!-- Footer -->
    <template #footer>
      <div
        class="px-xl py-m w-full"
        v-bind="getPt('footerWrapper', passThrough)"
      >
        <slot name="footer">
          <div class="inline-flex items-center justify-end gap-2.5 w-full">
            <slot name="adittionalButtonsLeft" />
            <MeridiaButton
              v-if="props.secondaryButtonFunction || props.secondaryButtonLabel"
              :type="returnMeridiaButtonType('secondary')"
              :size="meridiaButtonSize"
              :label="secondaryLabel"
              :disabled="primaryLoading || secondaryLoading"
              :loading="secondaryLoading"
              @click="onSecondary"
              :class="
                props.severityType === SEVERITY_TYPES.BASE_ERROR
                  ? '!text-critical-label-default'
                  : ''
              "
              v-bind="getPt('secondaryButton', passThrough)"
              :icon="props.secondaryButtonIcon"
              v-tooltip="props.secondaryButtonTooltip"
            />
            <MeridiaButton
              :type="returnMeridiaButtonType('primary')"
              :size="meridiaButtonSize"
              :label="primaryLabel"
              :disabled="primaryLoading || secondaryLoading"
              :loading="primaryLoading"
              @click="onPrimary"
              v-bind="getPt('primaryButton', passThrough)"
              :icon="props.primaryButtonIcon"
              v-tooltip="props.primaryButtonTooltip"
            />
          </div>
        </slot>
      </div>
    </template>
  </PrimeDialog>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import PrimeDialog from 'primevue/dialog';
import MeridiaButton from '../button/MeridiaButton.vue';
import MeridiaCheckbox from '../checkbox/MeridiaCheckbox.vue';
import MeridiaRadioButton from '../radioButton/MeridiaRadioButton.vue';
import MeridiaIcon from '../icon/MeridiaIcon.vue';
import {
  MeridiaDialogPassThroughOptions,
  MeridiaDialogProps,
  SEVERITY_TYPES,
  DIALOG_TYPES,
} from './types';
import { usePassThrough } from 'primevue/passthrough';
import type { PassThrough } from '@primevue/core';
import { getPt } from '../utils';

const model = defineModel<boolean>({ required: true });
const selected = defineModel<string | undefined>('selected');
const selectedList = defineModel<string[]>('selectedList', { default: [] });

const props = defineProps<MeridiaDialogProps>();

const visible = computed({
  get: () => !!model.value,
  set: (v: boolean) => (model.value = v),
});

const dialogId = `meridia-dialog-${Math.random().toString(36).slice(2)}`;

const selectedListMap = ref<Record<string, boolean>>({});

const primaryLoading = ref(false);
const secondaryLoading = ref(false);

onMounted(() => {
  // initialize checkbox map from v-model:selectedList
  const map: Record<string, boolean> = {};
  (props.options || []).forEach((o) => {
    map[o.value] = selectedList.value?.includes(o.value) || false;
  });
  selectedListMap.value = map;
});

watch(
  () => selectedListMap.value,
  (map) => {
    if (props.type !== DIALOG_TYPES.MULTIPLE_SELECT) return;
    const list = Object.entries(map)
      .filter(([, checked]) => checked)
      .map(([val]) => val);
    selectedList.value = list;
  },
  { deep: true }
);

function close() {
  visible.value = false;
}

async function onPrimary() {
  await props.primaryButtonFunction(close, primaryLoading);
}

async function onSecondary() {
  if (!props.secondaryButtonFunction) return;
  await props.secondaryButtonFunction(close, secondaryLoading);
}

const handleEscapeKey = (event: KeyboardEvent) => {
  if ((event.key === 'Escape' || event.key === 'Esc') && visible.value) {
    event.preventDefault();
    close();
  }
};

watch(
  () => visible.value,
  (isVisible) => {
    if (isVisible) {
      window.addEventListener('keydown', handleEscapeKey);
    } else {
      window.removeEventListener('keydown', handleEscapeKey);
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  window.removeEventListener('keydown', handleEscapeKey);
});

const iconName = computed(() => {
  switch (props.severityType) {
    case SEVERITY_TYPES.SUCCESS:
      return 'check_circle';
    case SEVERITY_TYPES.WARNING:
      return 'warning';
    case SEVERITY_TYPES.ERROR:
      return 'error';
    case SEVERITY_TYPES.INFO:
      return 'info';
    default:
      return '';
  }
});

const headerClasses = computed(() => {
  const base = ['w-full', 'pt-xl', 'pb-m', 'px-xl'];
  switch (props.severityType) {
    case SEVERITY_TYPES.SUCCESS:
      base.push('bg-primary-surface-muted');
      break;
    case SEVERITY_TYPES.WARNING:
      base.push('bg-high-surface-disabled');
      break;
    case SEVERITY_TYPES.ERROR:
      base.push('bg-critical-surface-disabled');
      break;
    case SEVERITY_TYPES.INFO:
      // Using tailwind blues until info tokens exist
      base.push('bg-blue-50');
      break;
    case SEVERITY_TYPES.BASE_SUCCESS:
    case SEVERITY_TYPES.BASE_ERROR:
    default:
      base.push('bg-neutral-surface-subtle');
      break;
  }
  return base;
});

const titleClasses = computed(() => {
  const base = ['uppercase', 'text-caption-xlarge'];
  switch (props.severityType) {
    case SEVERITY_TYPES.SUCCESS:
      base.push('text-primary-label-default');
      break;
    case SEVERITY_TYPES.WARNING:
      base.push('text-high-label-default');
      break;
    case SEVERITY_TYPES.ERROR:
      base.push('text-critical-label-default');
      break;
    case SEVERITY_TYPES.INFO:
      base.push('text-blue-500');
      break;
    case SEVERITY_TYPES.BASE_SUCCESS:
    case SEVERITY_TYPES.BASE_ERROR:
    default:
      base.push('text-neutral-text-body');
      break;
  }
  return base;
});

const returnMeridiaButtonType = (buttonType: 'primary' | 'secondary') => {
  switch (props.severityType) {
    case SEVERITY_TYPES.BASE_SUCCESS:
      return buttonType === 'primary' ? 'primary' : 'tertiary';
    case SEVERITY_TYPES.BASE_ERROR:
      return buttonType === 'primary' ? 'destructive' : 'tertiary';
    default:
      return buttonType === 'primary' ? 'primary' : 'tertiary';
  }
};

const meridiaButtonSize = computed(() => {
  switch (props.severityType) {
    case SEVERITY_TYPES.BASE_SUCCESS:
    case SEVERITY_TYPES.BASE_ERROR:
      return 'base';
    default:
      return 'mini';
  }
});

const primaryLabel = computed(() => props.primaryButtonLabel || 'Confirm');
const secondaryLabel = computed(() => props.secondaryButtonLabel || 'Cancel');

const passThrough = (usePassThrough(
  props.pt,
  {
    root: {
      class:
        'p-0 rounded-lg shadow-medium overflow-hidden border-none w-[350px]',
    },
    header: {
      class: 'p-0',
    },
    content: {
      class: 'p-0',
    },
    footer: {
      class: 'p-0',
    },
  } as MeridiaDialogPassThroughOptions,
  {
    mergeSections: false,
    mergeProps: true,
  }
) ?? {}) as PassThrough<MeridiaDialogPassThroughOptions>;
</script>

<style lang="scss" scoped></style>
